import React, { PureComponent } from 'react'
import styled, { keyframes } from 'styled-components'
import { IMountainPlayerData } from '../../helpers/data/mountainPlayer'
import { IMountainSortedPlayer } from '../../helpers/data/mountainSortedPlayer'
import Celebration from './components/Celebration'
import Label from '../../components/label/Label'

import dashboardAssetsProvider from '../../helpers/assets/dashboardAssetsProvider'
import timings from '../../helpers/timings/timings'
import Asset from '../../components/asset/Asset'
import { IAssetData } from '../../helpers/data/assetData'

const Container = styled.div`
	width: 100%;
	height: 100%;
	background-color: white;
`
const Wrapper = styled.div`
	width: 512px;
	height: 700px;
	overflow: hidden;
	transform-origin: 0 0;
	position: relative;
`

const BackgroundImage = styled.div`
	width: 100%;
	height: 100%;
	overflow: hidden;
	position: absolute;
	top: 0;
	left: 0;
`

const BackgroundGradient = styled.div`
	width: 100%;
	height: 100%;
	overflow: hidden;
	transition: opacity ${(timings.questionTransition * 3) / 5}s ease-out;
	position: absolute;
	top: 0;
	left: 0;
`

const Players = styled.div`
	width: 100%;
	height: 100%;
	position: absolute;
	bottom: 0;
	left: 0;
`

const Labels = styled.div`
	width: 100%;
	height: 100%;
	position: absolute;
	bottom: 0;
	left: 0;
`

const climbingAnim = keyframes`
	0% {
		transform: translate(0, 0) rotate(-10deg) ;
		animation-timing-function: ease-out;
	}
	25% {
		transform: translate(0, -6px) rotate(0deg) ;
		animation-timing-function: ease-in;
	}
	50% {
		transform: translate(0, 0px) rotate(10deg) ;
		animation-timing-function: ease-in;
	}
	75% {
		transform: translate(0, -6px) rotate(0deg) ;
		animation-timing-function: ease-in;
	}
	100% {
		transform: translate(0, 0) rotate(-10deg) ;
	}
`

const idleAnim = keyframes`
	0% {
		transform: translate(0, 0);
		animation-timing-function: ease-out;
	}
	50% {
		transform: translate(0, -2px);
		animation-timing-function: ease-in;
	}
	100% {
		transform: translate(0, 0);
	}
`

const Player = styled.div`
	width: 100%;
	height: 100%;
	transform-origin: 50% 100%;
	position: relative;
	&.climbing {
		animation: ${climbingAnim} 0.35s;
		animation-delay: ${timings.questionReveal}s;
		animation-iteration-count: infinite;
	}
`

const PlayerImg = styled.div`
	width: 100%;
	height: 100%;
	vertical-align: top;
	&.animated {
		animation: ${idleAnim} 0.4s;
		animation-iteration-count: infinite;
	}
`

const PlayerAssetsContainer = styled.div`
	width: 100%;
	height: 100%;
	text-align: center;
	position: relative;
	top: 0;
	left: 0;
`

const PlayerAsset = styled.div`
	width: 100%;
	height: 100%;
	position: absolute;
	top: 0;
	left: 0;
`

const PlayerLabelContainer = styled.div`
	font-size: 16px;
	max-width: 200px;
	transform: translate(22px, calc(-100% - 10px));
	position: absolute;
	top: 0;
	left: 0;
`

const PlayerContainer = styled.div`
	transition: transform 3s ease-out;
	transform: translate(0, 0);
`

const PlayerLane = styled.div`
	position: absolute;
	top: 0;
	left: 0;
`

const Landscape = styled.div`
	width: 100%;
	height: 100%;
	transition: transform 3s ease-out;
	transform: translate(0, 0);
`

const Segment = styled.div`
	width: 100%;
	height: 100%;
	position: absolute;
	bottom: 0;
	left: 0;
`

const CelebrationContainer = styled.div`
	width: 100%;
	height: 100%;
	pointer-events: none;
	position: absolute;
	bottom: 0;
	left: 0;
`
const MiscContainer = styled.div`
	width: 100%;
	height: 100%;
	position: absolute;
	bottom: 0;
	left: 0;
`

const ScreenReaderOnly = styled.div`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`

export interface ISkin {
	background: IAssetData
	backgroundGradient: IAssetData
	celebration: IAssetData[]
	miscellaneous: IAssetData[]
}

interface IDashboardProps {
	isCorrect: boolean
	isQuestionFinished: boolean
	players: IMountainPlayerData[]
	questionIndex: number
	totalActivities: number
	scale: number
	playerUid: string
	skin: ISkin
	currentPlayerRank: number
	sortedPlayers: IMountainSortedPlayer[]
	areAnimationsEnabled?: boolean
}

interface IDashboardState {
	slideAssets: ISlideAssetData[][]
	hoveredPlayer: string
}

interface ISlideAssetData {
	h: number
	asset: IAssetData
	w: number
	x: number
	y: number
}

class Dashboard extends PureComponent<IDashboardProps, IDashboardState> {
	private container: HTMLDivElement = document.createElement('div')

	constructor(props: IDashboardProps) {
		super(props)

		this.state = {
			slideAssets: [],
			hoveredPlayer: '',
		}
	}

	static defaultProps = {
		totalActivities: 2,
	}

	static getDerivedStateFromProps(props: IDashboardProps, state: IDashboardState) {
		if (state.slideAssets.length < props.questionIndex + 2) {
			const slideAssets = [...state.slideAssets]
			while (slideAssets.length < props.questionIndex + 2) {
				slideAssets.push(dashboardAssetsProvider(props.skin.miscellaneous))
			}

			return {
				slideAssets,
			}
		} else {
			return state
		}
	}


	getOrdinalSuffix(rank: number): string {
		const lastDigit = rank % 10
		const lastTwoDigits = rank % 100

		if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
			return 'th'
		}

		switch (lastDigit) {
			case 1: return 'st'
			case 2: return 'nd'
			case 3: return 'rd'
			default: return 'th'
		}
	}

	buildCurrentPlayerAnnouncement(): string {
		const currentPlayer = this.props.players.find(p => p.playerUID === this.props.playerUid)
		if (!currentPlayer || !this.props.currentPlayerRank) {
			return ''
		}

		const ordinalSuffix = this.getOrdinalSuffix(this.props.currentPlayerRank)
		return `${currentPlayer.label} ${this.props.currentPlayerRank}${ordinalSuffix} place`
	}

	buildScreenReaderMountain() {
		return (
			<ScreenReaderOnly>
				<h2>Mountain Race Placements</h2>
				<div role="status" aria-live="polite" aria-atomic="true">
					{this.buildCurrentPlayerAnnouncement()}
				</div>
				<ol aria-label="Mountain Race Placements">
					{this.props.sortedPlayers.map((player, index) => {
						const rank = index + 1
						return (
							<li key={player.playerUID}>
								{rank}{this.getOrdinalSuffix(rank)} place: {player.label}
							</li>
						)
					})}
				</ol>
			</ScreenReaderOnly>
		)
	}

	buildLabels(players: IMountainPlayerData[], areClimbing: boolean) {
		const totalPlayers = players.length

		// players size is calculated between a minimum of 4 and a maximum of 20 players.
		// If there are less or more, size caps at those number.
		const playerContainerSize = 0.5 + 0.5 * (1 - Math.max(0, Math.min(20, totalPlayers) - 4) / 16)

		const transitionDelay = areClimbing ? `${timings.questionReveal}s` : '0s'

		return players.map((player: IMountainPlayerData) => {
			const playerX = totalPlayers === 1 ? 150 / 2 : (150 * player.lane) / (totalPlayers - 1)
			const playerY = totalPlayers === 1 ? 100 / 2 : (100 * player.lane) / (totalPlayers - 1)
			const label =
				player.playerUID === this.props.playerUid || player.playerUID === this.state.hoveredPlayer ? player.label : ''
			return (
				<PlayerLane
					key={player.playerUID}
					style={{
						transform: `translateY(700px) translate(${30 + playerX}px, ${-170 + playerY}px)`,
					}}
				>
					<PlayerContainer
						onMouseOver={() => {
							this.setState({ hoveredPlayer: player.playerUID })
						}}
						onMouseOut={() => {
							this.setState((state: IDashboardState) => {
								if (state.hoveredPlayer === player.playerUID) {
									return {
										hoveredPlayer: '',
									}
								} else {
									return null
								}
							})
						}}
						style={{
							transform: `translate(${player.pos * 275}px, ${player.pos * -386}px)`,
							transitionDelay,
						}}
					>
						<Player className={this.props.areAnimationsEnabled ? (areClimbing ? 'climbing' : 'idle') : ''}>
							<PlayerImg
								className={this.props.areAnimationsEnabled ? 'animated' : ''}
								style={{
									width: `${44 * playerContainerSize}px`,
									height: `${58 * playerContainerSize}px`,
								}}
							></PlayerImg>
							{label && (
								<PlayerLabelContainer>
									<Label text={label} />
								</PlayerLabelContainer>
							)}
						</Player>
					</PlayerContainer>
				</PlayerLane>
			)
		})
	}

	buildPlayers(players: IMountainPlayerData[], areClimbing: boolean) {
		const totalPlayers = players.length

		// players size is calculated between a minimum of 4 and a maximum of 20 players.
		// If there are less or more, size caps at those number.
		const playerContainerSize = 0.5 + 0.5 * (1 - Math.max(0, Math.min(20, totalPlayers) - 4) / 16)

		const transitionDelay = areClimbing ? `${timings.questionReveal}s` : '0s'

		return players.map((player: IMountainPlayerData) => {
			const playerX = totalPlayers === 1 ? 150 / 2 : (150 * player.lane) / (totalPlayers - 1)
			const playerY = totalPlayers === 1 ? 100 / 2 : (100 * player.lane) / (totalPlayers - 1)
			return (
				<PlayerLane
					key={player.playerUID}
					style={{
						transform: `translateY(700px) translate(${30 + playerX}px, ${-170 + playerY}px)`,
					}}
				>
					<PlayerContainer
						style={{
							transform: `translate(${player.pos * 275}px, ${player.pos * -386}px)`,
							transitionDelay,
						}}
					>
						<Player className={this.props.areAnimationsEnabled ? (areClimbing ? 'climbing' : 'idle') : ''}>
							<PlayerImg
								className={this.props.areAnimationsEnabled ? 'animated' : ''}
								style={{
									width: `${44 * playerContainerSize}px`,
									height: `${58 * playerContainerSize}px`,
								}}
							>
								<PlayerAssetsContainer>
									{player.avatarAsset.map((asset: IAssetData, index: number) => (
										<PlayerAsset key={index}>
											<Asset asset={asset} uid={player.playerUID + player.avatarId} />
										</PlayerAsset>
									))}
								</PlayerAssetsContainer>
							</PlayerImg>
						</Player>
					</PlayerContainer>
				</PlayerLane>
			)
		})
	}

	render() {
		const index = this.props.isQuestionFinished ? 1 : 0
		const opacity = (this.props.questionIndex + 1) / this.props.totalActivities
		const transitionDuration = this.props.isQuestionFinished ? `${(timings.questionTransition * 3) / 5}s` : '0s'

		const canShowCelebration = this.props.isQuestionFinished && this.props.isCorrect

		return (
			<Container
				ref={(elem: HTMLDivElement) => {
					this.container = elem
				}}
			>
				{this.buildScreenReaderMountain()}
				<Wrapper style={{ transform: `scale(${this.props.scale}, ${this.props.scale})` }}>
					<BackgroundImage>
						<Asset asset={this.props.skin.background} />
					</BackgroundImage>
					<BackgroundGradient
						style={{
							opacity,
						}}
					>
						<Asset asset={this.props.skin.backgroundGradient} />
					</BackgroundGradient>
					<Landscape
						style={{
							transform: `translate(${-512 * index}px, ${700 * index}px)`,
							transitionDuration,
							transitionDelay: `${timings.questionReveal}s`,
						}}
					>
						<Segment className={'Segment1'}>
							{this.state.slideAssets[this.props.questionIndex].map((slideData: ISlideAssetData, index: number) => (
								<MiscContainer
									key={index}
									style={{
										width: `${slideData.w}px`,
										height: `${slideData.h}px`,
										position: 'absolute',
										transform: 'translate(-50%, -100%)',
										left: `${slideData.x}px`,
										top: `${slideData.y}px`,
									}}
								>
									<Asset asset={slideData.asset} />
								</MiscContainer>
							))}
						</Segment>
						<Segment
							className={'Segment2'}
							style={{
								transform: `translate(${512}px, ${-700}px)`,
							}}
						>
							{this.state.slideAssets[this.props.questionIndex + 1].map((slideData: ISlideAssetData, index: number) => (
								<MiscContainer
									key={index}
									style={{
										width: `${slideData.w}px`,
										height: `${slideData.h}px`,
										position: 'absolute',
										transform: 'translate(-50%, -100%)',
										left: `${slideData.x}px`,
										top: `${slideData.y}px`,
									}}
								>
									<Asset asset={slideData.asset} />
								</MiscContainer>
							))}
						</Segment>
					</Landscape>
					<Players>{this.buildPlayers(this.props.players, this.props.isQuestionFinished)}</Players>
					<Labels>{this.buildLabels(this.props.players, this.props.isQuestionFinished)}</Labels>
					{canShowCelebration && (
						<CelebrationContainer>
							<Celebration celebration={this.props.skin.celebration} />
						</CelebrationContainer>
					)}
				</Wrapper>
			</Container>
		)
	}
}

export default Dashboard
